import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { MyStore } from "@shared/types";
import { api } from "@/lib/api";
import { setActiveStoreId } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STORAGE_KEY = "active_store_id";

interface StoreListResponse {
  stores: MyStore[];
  limit: number | null;
  used: number;
  canAdd: boolean;
  allowCustomDomain: boolean;
  /** Stores advertising on HQ surfaces (Iklan HQ); absent from older Workers. */
  advertiserStoreIds?: string[];
}

interface StoreContextValue {
  stores: MyStore[];
  activeStore: MyStore | null;
  loading: boolean;
  /** null = unlimited. */
  limit: number | null;
  used: number;
  canAdd: boolean;
  allowCustomDomain: boolean;
  /** The active store advertises on HQ surfaces, so it makes FYP posts for its ads. */
  advertiser: boolean;
  /** Switch the active store (persists + updates the API header). */
  selectStore: (id: string) => void;
  /** Re-fetch the store list (after create/rename/archive). */
  refresh: () => Promise<void>;
}

const StoreContext = createContext<StoreContextValue | null>(null);

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function StoreProvider({ children }: { children: ReactNode }) {
  const { isVerified } = useAuth();
  const [stores, setStores] = useState<MyStore[]>([]);
  const [meta, setMeta] = useState({ limit: null as number | null, used: 0, canAdd: true, allowCustomDomain: false });
  const [advertisers, setAdvertisers] = useState<string[]>([]);
  const [activeId, setActiveId] = useState<string | null>(() => readStored());
  const [loading, setLoading] = useState(true);

  // Keep the API header in lockstep with the active store id.
  useEffect(() => {
    setActiveStoreId(activeId);
  }, [activeId]);

  const load = useCallback(async () => {
    const res = await api.get<StoreListResponse>("/stores");
    setStores(res.stores);
    setMeta({
      limit: res.limit,
      used: res.used,
      canAdd: res.canAdd,
      allowCustomDomain: res.allowCustomDomain,
    });
    setAdvertisers(res.advertiserStoreIds ?? []);
    // Resolve the active store: keep the stored choice if still valid, else the
    // default (first) store.
    setActiveId((cur) => {
      const valid = cur && res.stores.some((s) => s.id === cur) ? cur : (res.stores[0]?.id ?? null);
      return valid;
    });
  }, []);

  useEffect(() => {
    if (!isVerified) {
      setStores([]);
      setActiveId(null);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    load()
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [isVerified, load]);

  const selectStore = useCallback((id: string) => {
    setActiveId(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      /* ignore quota/private-mode */
    }
    setActiveStoreId(id);
  }, []);

  const activeStore = useMemo(
    () => stores.find((s) => s.id === activeId) ?? stores[0] ?? null,
    [stores, activeId],
  );

  const value = useMemo<StoreContextValue>(
    () => ({
      stores,
      activeStore,
      loading,
      limit: meta.limit,
      used: meta.used,
      canAdd: meta.canAdd,
      allowCustomDomain: meta.allowCustomDomain,
      advertiser: !!activeStore && advertisers.includes(activeStore.id),
      selectStore,
      refresh: load,
    }),
    [stores, activeStore, loading, meta, advertisers, selectStore, load],
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useStores(): StoreContextValue {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useStores must be used within StoreProvider");
  return ctx;
}
